import { supabase } from '../lib/supabase';

export const quoteService = {
  // Get all quotes
  async getQuotes() {
    try {
      const { data, error } = await supabase
        ?.from('quotes')
        ?.select(`
          *,
          client:clients(id, first_name, last_name, email),
          created_by:user_profiles(id, full_name, email),
          quote_appliances(appliance_type, included)
        `)
        ?.order('created_at', { ascending: false });
      
      if (error) {
        return { quotes: [], error: error?.message };
      }
      
      return { quotes: data || [], error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          quotes: [], 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { quotes: [], error: 'Failed to load quotes.' };
    }
  },

  // Get quote by ID
  async getQuote(id) {
    try {
      const { data, error } = await supabase
        ?.from('quotes')
        ?.select(`
          *,
          client:clients(*),
          created_by:user_profiles(id, full_name, email),
          quote_appliances(appliance_type, included),
          quote_audit_trail(
            action,
            old_values,
            new_values,
            performed_at,
            performed_by:user_profiles(full_name)
          )
        `)
        ?.eq('id', id)
        ?.single();
      
      if (error) {
        return { quote: null, error: error?.message };
      }
      
      return { quote: data, error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          quote: null, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { quote: null, error: 'Failed to load quote.' };
    }
  },

  // Create new quote
  async createQuote(quoteData, appliances = []) {
    try {
      const { data: quote, error: quoteError } = await supabase
        ?.from('quotes')
        ?.insert([quoteData])
        ?.select()
        ?.single();
      
      if (quoteError) {
        return { quote: null, error: quoteError?.message };
      }

      // Create appliances if provided
      if (appliances?.length > 0) {
        const applianceData = appliances?.map(appliance => ({
          quote_id: quote?.id,
          appliance_type: appliance?.type,
          included: appliance?.included || false
        }));

        const { error: applianceError } = await supabase
          ?.from('quote_appliances')
          ?.insert(applianceData);

        if (applianceError) {
          // Log error but don't fail the quote creation console.error('Failed to create appliances:', applianceError);
        }
      }
      
      return { quote, error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          quote: null, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { quote: null, error: 'Failed to create quote.' };
    }
  },

  // Update quote
  async updateQuote(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('quotes')
        ?.update(updates)
        ?.eq('id', id)
        ?.select()
        ?.single();
      
      if (error) {
        return { quote: null, error: error?.message };
      }
      
      return { quote: data, error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          quote: null, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { quote: null, error: 'Failed to update quote.' };
    }
  },

  // Update quote appliances
  async updateQuoteAppliances(quoteId, appliances) {
    try {
      // First, delete existing appliances
      const { error: deleteError } = await supabase
        ?.from('quote_appliances')
        ?.delete()
        ?.eq('quote_id', quoteId);
      
      if (deleteError) {
        return { error: deleteError?.message };
      }

      // Then, insert new appliances
      if (appliances?.length > 0) {
        const applianceData = appliances?.map(appliance => ({
          quote_id: quoteId,
          appliance_type: appliance?.type,
          included: appliance?.included || false
        }));

        const { error: insertError } = await supabase
          ?.from('quote_appliances')
          ?.insert(applianceData);

        if (insertError) {
          return { error: insertError?.message };
        }
      }
      
      return { error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { error: 'Failed to update appliances.' };
    }
  },

  // Delete quote
  async deleteQuote(id) {
    try {
      const { error } = await supabase
        ?.from('quotes')
        ?.delete()
        ?.eq('id', id);
      
      if (error) {
        return { error: error?.message };
      }
      
      return { error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { error: 'Failed to delete quote.' };
    }
  },

  // Sign quote
  async signQuote(id, signatureData) {
    try {
      const { data, error } = await supabase
        ?.from('quotes')
        ?.update({
          signature_data: signatureData?.signature,
          signed_by: signatureData?.signedBy,
          signed_at: new Date()?.toISOString(),
          status: 'approved'
        })
        ?.eq('id', id)
        ?.select()
        ?.single();
      
      if (error) {
        return { quote: null, error: error?.message };
      }
      
      return { quote: data, error: null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          quote: null, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { quote: null, error: 'Failed to sign quote.' };
    }
  },

  // Calculate quote price
  calculateQuotePrice(quoteData, systemSettings) {
    try {
      const pricing = systemSettings?.pricing_config || {};
      const multipliers = systemSettings?.client_multipliers || {};
      const vatRates = systemSettings?.vat_rates || {};

      let basePrice = 0;

      // Kitchen elements pricing
      basePrice += (quoteData?.upper_cabinets || 0) * (pricing?.upperCabinet || 120);
      basePrice += (quoteData?.lower_cabinets || 0) * (pricing?.lowerCabinet || 180);
      basePrice += (quoteData?.columns || 0) * (pricing?.column || 250);
      basePrice += (quoteData?.upper_corners || 0) * (pricing?.upperCorner || 150);
      basePrice += (quoteData?.lower_corners || 0) * (pricing?.lowerCorner || 200);
      basePrice += (quoteData?.baseboards || 0) * (pricing?.baseboard || 15);
      basePrice += (quoteData?.jambs || 0) * (pricing?.jamb || 45);
      basePrice += (quoteData?.fillers || 0) * (pricing?.filler || 35);
      basePrice += (quoteData?.side_panels || 0) * (pricing?.sidePanel || 65);
      basePrice += (quoteData?.backsplash || 0) * (pricing?.backsplash || 25);

      // Worktop pricing
      const worktopPrices = { macif: 180, compact: 220, laminate: 95, granite: 300, marble: 350, quartz: 280 };
      const shapePrices = { simple: 1, l_shape: 1.2, u_shape: 1.4 };
      
      if (quoteData?.worktop_type && quoteData?.installation_shape) {
        const baseWorktopPrice = worktopPrices?.[quoteData?.worktop_type] || 0;
        const shapeMultiplier = shapePrices?.[quoteData?.installation_shape] || 1;
        basePrice += baseWorktopPrice * shapeMultiplier * 3; // Assuming 3ML average
        basePrice += (quoteData?.cutouts || 0) * 45;
      }

      // Additional services
      if (quoteData?.plumbing_modification) basePrice += 150;
      if (quoteData?.lighting_installation) basePrice += 200;
      if (quoteData?.electrical_modification) basePrice += 180;

      // Appliance integration (would need appliances data)
      // This would be calculated separately based on quote_appliances

      // Client category multiplier
      const categoryMultiplier = multipliers?.[quoteData?.client_category] || 1;
      const subtotal = basePrice * categoryMultiplier;

      // VAT calculation
      const vatRate = vatRates?.[quoteData?.vat_rate] || 0.2;
      const vatAmount = subtotal * vatRate;
      const totalPrice = subtotal + vatAmount;

      return {
        basePrice: parseFloat(basePrice?.toFixed(2)),
        subtotal: parseFloat(subtotal?.toFixed(2)),
        vatAmount: parseFloat(vatAmount?.toFixed(2)),
        totalPrice: parseFloat(totalPrice?.toFixed(2))
      };
    } catch (error) {
      console.error('Price calculation error:', error);
      return {
        basePrice: 0,
        subtotal: 0,
        vatAmount: 0,
        totalPrice: 0
      };
    }
  }
};

export default quoteService;